import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, Key, CheckCircle, XCircle, Loader, ExternalLink, RefreshCw, Cpu, Server, ShieldCheck, Globe } from 'lucide-react';
import ApiService from '../../services/api';

const PROVIDER_FALLBACK_CATALOG = [
  {
    id: 'cursor',
    label: 'Cursor Agent (SDK)',
    description: 'Managed Cursor Agent powered by Cursor SDK with multi-model and custom API key support',
    defaultModel: 'auto',
    models: [
      'auto',
      'composer-2.5',
      'claude-3.7-sonnet',
      'claude-3.5-sonnet',
      'gpt-4o',
      'gpt-4.5',
      'o3-mini',
      'o1',
      'deepseek-r1',
      'deepseek-v3',
      'gemini-2.0-flash',
    ],
    requiresApiKey: false,
    requiresBaseUrl: false,
    placeholderApiKey: 'Optional: Custom CURSOR_API_KEY (uses system key if blank)',
    docsUrl: 'https://cursor.com',
  },
  {
    id: 'gemini',
    label: 'Google AGY / Gemini',
    description: 'Google AGY / Gemini 3.6 Flash, 3.1 Flash-Lite, 3.7 Flash via Google Antigravity & AI Studio (supports AQ.… and AIzaSy… keys)',
    defaultModel: 'gemini-3.6-flash',
    models: [
      'gemini-3.6-flash',
      'gemini-3.1-flash-lite',
      'gemini-3.7-flash',
      'gemini-3.5-flash',
    ],
    defaultBaseUrl: 'https://generativelanguage.googleapis.com/v1beta/openai',
    requiresApiKey: true,
    requiresBaseUrl: false,
    placeholderApiKey: 'AQ.… or AIzaSy…',
    docsUrl: 'https://ai.google.dev/gemini-api/docs',
  },
  {
    id: 'anthropic',
    label: 'Anthropic (Claude)',
    description: 'Claude 3.7 Sonnet, Claude 3.5 Sonnet, Haiku, Opus directly via Anthropic API',
    defaultModel: 'claude-3-7-sonnet-20250219',
    models: [
      'claude-3-7-sonnet-20250219',
      'claude-3-5-sonnet-20241022',
      'claude-3-5-haiku-20241022',
      'claude-3-opus-20240229',
    ],
    defaultBaseUrl: 'https://api.anthropic.com/v1',
    requiresApiKey: true,
    requiresBaseUrl: false,
    supportsWorkspaceId: true,
    workspaceIdLabel: 'Anthropic Workspace ID (anthropic-workspace-id)',
    workspaceIdPlaceholder: 'wrkspc_… or workspace ID',
    placeholderApiKey: 'sk-ant-api03-…',
    docsUrl: 'https://docs.anthropic.com/claude/reference/getting-started-with-the-api',
  },
  {
    id: 'openai',
    label: 'OpenAI (GPT-4o, o3-mini)',
    description: 'GPT-4o, GPT-4o-mini, o1, o3-mini directly via OpenAI API',
    defaultModel: 'gpt-4o',
    models: [
      'gpt-4o',
      'gpt-4o-mini',
      'o3-mini',
      'o1',
      'gpt-4-turbo',
    ],
    defaultBaseUrl: 'https://api.openai.com/v1',
    requiresApiKey: true,
    requiresBaseUrl: false,
    placeholderApiKey: 'sk-proj-…',
    docsUrl: 'https://platform.openai.com/docs/api-reference',
  },
  {
    id: 'openrouter',
    label: 'OpenRouter',
    description: 'Unified gateway for 100+ models (Claude, OpenAI, DeepSeek, Llama, Gemini)',
    defaultModel: 'anthropic/claude-3.7-sonnet',
    models: [
      'anthropic/claude-3.7-sonnet',
      'anthropic/claude-3.5-sonnet',
      'openai/gpt-4o',
      'deepseek/deepseek-r1',
      'deepseek/deepseek-chat',
      'google/gemini-2.0-flash-001',
      'meta-llama/llama-3.3-70b-instruct',
    ],
    defaultBaseUrl: 'https://openrouter.ai/api/v1',
    requiresApiKey: true,
    requiresBaseUrl: false,
    placeholderApiKey: 'sk-or-v1-…',
    docsUrl: 'https://openrouter.ai/docs',
  },
  {
    id: 'groq',
    label: 'Groq',
    description: 'Ultra high-speed Llama 3 and Mixtral inference',
    defaultModel: 'llama-3.3-70b-versatile',
    models: [
      'llama-3.3-70b-versatile',
      'llama-3.1-8b-instant',
      'mixtral-8x7b-32768',
      'deepseek-r1-distill-llama-70b',
    ],
    defaultBaseUrl: 'https://api.groq.com/openai/v1',
    requiresApiKey: true,
    requiresBaseUrl: false,
    placeholderApiKey: 'gsk_…',
    docsUrl: 'https://console.groq.com/docs/models',
  },
  {
    id: 'ollama',
    label: 'Ollama (Local / Self-hosted)',
    description: 'Run open-source models locally or on a private server (Llama 3, Qwen, DeepSeek, Mistral)',
    defaultModel: 'llama3:latest',
    models: [
      'llama3:latest',
      'qwen2.5:latest',
      'deepseek-r1:latest',
      'mistral:latest',
    ],
    defaultBaseUrl: 'http://localhost:11434/v1',
    requiresApiKey: false,
    requiresBaseUrl: true,
    placeholderApiKey: 'Optional for local Ollama',
    docsUrl: 'https://ollama.com',
  },
  {
    id: 'custom',
    label: 'Custom OpenAI-compatible API',
    description: 'Any provider with an OpenAI-compatible /v1/chat/completions endpoint (vLLM, LM Studio, Azure, etc.)',
    defaultModel: 'default',
    models: [],
    defaultBaseUrl: 'https://api.example.com/v1',
    requiresApiKey: false,
    requiresBaseUrl: true,
    placeholderApiKey: 'Bearer token / API key',
    docsUrl: '',
  },
];

export default function AdminAiProviderTab({ refreshTick: _refreshTick = 0 }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const [catalog, setCatalog] = useState(PROVIDER_FALLBACK_CATALOG);
  const [selectedProvider, setSelectedProvider] = useState('cursor');
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [hasApiKey, setHasApiKey] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [configuredAt, setConfiguredAt] = useState(null);
  const [activeSource, setActiveSource] = useState('system');
  const [isConfigured, setIsConfigured] = useState(true);
  const [effectiveModel, setEffectiveModel] = useState('');

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ApiService.getAdminAiProviderSettings();
      if (Array.isArray(res?.catalog) && res.catalog.length > 0) {
        setCatalog(res.catalog);
      }
      const cur = res?.current || {};
      setSelectedProvider(cur.provider || 'cursor');
      setModel(cur.model || '');
      setBaseUrl(cur.baseUrl || '');
      setWorkspaceId(cur.workspaceId || '');
      setHasApiKey(!!cur.hasApiKey);
      setConfiguredAt(cur.configuredAt || null);
      setActiveSource(cur.source || 'global');
      setIsConfigured(cur.isConfigured !== false);
      setEffectiveModel(cur.effectiveModel || '');
    } catch (err) {
      setError(err?.message || 'Failed to load global AI provider settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const currentMeta = catalog.find((c) => c.id === selectedProvider) || catalog[0] || PROVIDER_FALLBACK_CATALOG[0];

  const handleProviderChange = (providerId) => {
    setSelectedProvider(providerId);
    setTestResult(null);
    setError(null);
    setSuccess(null);
    const meta = catalog.find((c) => c.id === providerId);
    if (meta) {
      setModel(meta.defaultModel || '');
      setBaseUrl(meta.defaultBaseUrl || '');
    }
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    setTestResult(null);

    try {
      const payload = {
        provider: selectedProvider,
        model: model.trim() || currentMeta?.defaultModel,
        baseUrl: baseUrl.trim(),
        workspaceId: workspaceId.trim(),
      };
      if (apiKey.trim()) {
        payload.apiKey = apiKey.trim();
      }

      const res = await ApiService.updateAdminAiProviderSettings(payload);
      setSuccess('Global AI provider updated successfully! All workspaces now default to this engine.');
      setApiKey('');
      setShowKey(false);
      if (res?.current) {
        setHasApiKey(!!res.current.hasApiKey);
        setConfiguredAt(res.current.configuredAt || new Date().toISOString());
        setActiveSource(res.current.source || 'global');
        setIsConfigured(res.current.isConfigured !== false);
        setEffectiveModel(res.current.effectiveModel || '');
      }
    } catch (err) {
      setError(err?.message || 'Failed to save global AI settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        provider: selectedProvider,
        apiKey: apiKey.trim(),
        model: model.trim() || currentMeta?.defaultModel,
        baseUrl: baseUrl.trim() || currentMeta?.defaultBaseUrl,
        workspaceId: workspaceId.trim(),
      };
      const res = await ApiService.testAdminAiProviderSettings(payload);
      setTestResult({
        ok: true,
        message: res?.message || 'Connection test succeeded!',
      });
    } catch (err) {
      setTestResult({
        ok: false,
        message: err?.message || 'Connection test failed',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleClearApiKey = async () => {
    if (!window.confirm('Clear stored Global API Key? Workspaces will fall back to server environment keys or fail if unconfigured.')) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await ApiService.updateAdminAiProviderSettings({
        provider: selectedProvider,
        model: model.trim() || currentMeta?.defaultModel,
        baseUrl: baseUrl.trim(),
        workspaceId: workspaceId.trim(),
        clearApiKey: true,
      });
      setHasApiKey(false);
      setApiKey('');
      setShowKey(false);
      setSuccess('Global API Key removed.');
      if (res?.current) {
        setIsConfigured(res.current.isConfigured !== false);
        setActiveSource(res.current.source || 'global');
      }
    } catch (err) {
      setError(err?.message || 'Failed to clear global API Key');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-400">
        <Loader className="w-5 h-5 animate-spin mr-2" />
        <span>Loading Global AI Provider settings…</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header Info */}
      <div className="border border-white/10 rounded-xl bg-surface-1 p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-teal-400" />
              <h2 className="text-base font-semibold text-slate-100">Global AI Provider Management</h2>
            </div>
            <p className="text-xs text-slate-400">
              Configure the default AI / LLM provider used platform-wide across all tenants and workspaces.
              Non-enterprise workspaces automatically inherit this engine.
            </p>
          </div>
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <button
              type="button"
              onClick={loadSettings}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-xs text-slate-300 transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Reload
            </button>
          </div>
        </div>

        {/* Status Pill Bar */}
        <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap items-center gap-4 text-xs">
          <div className="flex items-center gap-2">
            <span className="text-slate-400">Active Global Engine:</span>
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full font-medium bg-teal-500/10 text-teal-300 border border-teal-500/20">
              <Sparkles className="w-3 h-3" />
              {currentMeta?.label || selectedProvider}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-slate-400">Effective Model:</span>
            <span className="font-mono text-slate-200 bg-white/5 px-2 py-0.5 rounded border border-white/10">
              {effectiveModel || model || currentMeta?.defaultModel || 'auto'}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-slate-400">Status:</span>
            {isConfigured ? (
              <span className="inline-flex items-center gap-1 text-emerald-400">
                <CheckCircle className="w-3.5 h-3.5" /> Ready
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-amber-400">
                <XCircle className="w-3.5 h-3.5" /> Key Required
              </span>
            )}
          </div>

          {configuredAt && (
            <div className="text-[11px] text-slate-500 ml-auto tabular-nums">
              Last saved: {new Date(configuredAt).toLocaleString()}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-300 text-xs flex items-start gap-2.5">
          <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="p-4 rounded-xl border border-teal-500/30 bg-teal-500/10 text-teal-300 text-xs flex items-start gap-2.5">
          <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Provider Selector Grid */}
      <div className="border border-white/10 rounded-xl bg-surface-1 p-5 shadow-sm space-y-4">
        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400">
          Select Global AI Engine
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {catalog.map((p) => {
            const isSelected = selectedProvider === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => handleProviderChange(p.id)}
                className={`p-3.5 rounded-xl border text-left transition-all relative flex flex-col justify-between ${
                  isSelected
                    ? 'border-teal-500 bg-teal-500/10 shadow-sm ring-1 ring-teal-500/40'
                    : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.06] hover:border-white/20'
                }`}
              >
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className={`text-xs font-semibold ${isSelected ? 'text-teal-200' : 'text-slate-200'}`}>
                      {p.label}
                    </span>
                    {isSelected && <CheckCircle className="w-3.5 h-3.5 text-teal-400" />}
                  </div>
                  <p className="text-[11px] text-slate-400 line-clamp-2 leading-relaxed">
                    {p.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Configuration Form */}
      <form onSubmit={handleSave} className="border border-white/10 rounded-xl bg-surface-1 p-5 shadow-sm space-y-5">
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-100 flex items-center gap-2">
              <Cpu className="w-4 h-4 text-teal-400" />
              Configure {currentMeta?.label}
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              {currentMeta?.description}
            </p>
          </div>
          {currentMeta?.docsUrl && (
            <a
              href={currentMeta.docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-teal-400 hover:text-teal-300 flex items-center gap-1 hover:underline shrink-0"
            >
              Docs <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>

        {/* API Key Input */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-slate-300 flex items-center gap-1.5">
              <Key className="w-3.5 h-3.5 text-slate-400" />
              API Key {currentMeta?.requiresApiKey ? <span className="text-rose-400">*</span> : <span className="text-slate-500 font-normal">(Optional)</span>}
            </label>
            {hasApiKey && (
              <div className="flex items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1 text-emerald-400 text-[11px]">
                  <ShieldCheck className="w-3 h-3" /> Encrypted Key Stored
                </span>
                <button
                  type="button"
                  onClick={handleClearApiKey}
                  className="text-rose-400 hover:text-rose-300 text-[11px] underline"
                >
                  Clear Key
                </button>
              </div>
            )}
          </div>
          <div className="relative">
            <input
              type={showKey ? 'text' : 'password'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={hasApiKey ? '•••••••••••••••••••••••• (Leave blank to keep current key)' : (currentMeta?.placeholderApiKey || 'Enter API Key')}
              className="w-full text-xs font-mono px-3 py-2 pr-16 rounded-lg border border-white/10 bg-black/40 text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
            />
            <button
              type="button"
              onClick={() => setShowKey(!showKey)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-slate-400 hover:text-slate-200 px-2 py-0.5 rounded border border-white/10 bg-white/5"
            >
              {showKey ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        {/* Model Selection */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-slate-300 flex items-center gap-1.5">
            <Cpu className="w-3.5 h-3.5 text-slate-400" />
            Model
          </label>
          {Array.isArray(currentMeta?.models) && currentMeta.models.length > 0 ? (
            <div className="space-y-2">
              <select
                value={model || currentMeta.defaultModel}
                onChange={(e) => setModel(e.target.value)}
                className="w-full text-xs px-3 py-2 rounded-lg border border-white/10 bg-black/40 text-slate-200 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
              >
                {currentMeta.models.map((m) => (
                  <option key={m} value={m}>
                    {m} {m === currentMeta.defaultModel ? '(Default)' : ''}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <input
              type="text"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder={currentMeta?.defaultModel || 'e.g. gpt-4o or custom-model'}
              className="w-full text-xs px-3 py-2 rounded-lg border border-white/10 bg-black/40 text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
            />
          )}
        </div>

        {/* Base URL (Optional / Custom) */}
        {(currentMeta?.requiresBaseUrl || currentMeta?.defaultBaseUrl || selectedProvider === 'custom' || selectedProvider === 'ollama') && (
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-300 flex items-center gap-1.5">
              <Server className="w-3.5 h-3.5 text-slate-400" />
              API Base URL {currentMeta?.requiresBaseUrl ? <span className="text-rose-400">*</span> : <span className="text-slate-500 font-normal">(Optional)</span>}
            </label>
            <input
              type="text"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder={currentMeta?.defaultBaseUrl || 'https://api.example.com/v1'}
              className="w-full text-xs font-mono px-3 py-2 rounded-lg border border-white/10 bg-black/40 text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
            />
          </div>
        )}

        {/* Anthropic Workspace ID */}
        {currentMeta?.supportsWorkspaceId && (
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-300">
              {currentMeta.workspaceIdLabel || 'Workspace ID'} <span className="text-slate-500 font-normal">(Optional)</span>
            </label>
            <input
              type="text"
              value={workspaceId}
              onChange={(e) => setWorkspaceId(e.target.value)}
              placeholder={currentMeta.workspaceIdPlaceholder || 'wrkspc_…'}
              className="w-full text-xs font-mono px-3 py-2 rounded-lg border border-white/10 bg-black/40 text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
            />
          </div>
        )}

        {/* Test Result Message */}
        {testResult && (
          <div className={`p-3 rounded-lg border text-xs flex items-start gap-2 ${
            testResult.ok
              ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
              : 'border-rose-500/30 bg-rose-500/10 text-rose-300'
          }`}>
            {testResult.ok ? <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 shrink-0 mt-0.5" />}
            <div className="space-y-0.5">
              <div className="font-semibold">{testResult.ok ? 'Connection Verified' : 'Connection Failed'}</div>
              <div className="text-[11px] opacity-90">{testResult.message}</div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="pt-2 flex items-center justify-between border-t border-white/10">
          <button
            type="button"
            onClick={handleTest}
            disabled={testing || saving}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-xs font-medium text-slate-200 transition-colors disabled:opacity-50"
          >
            {testing ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 text-teal-400" />}
            {testing ? 'Testing Connection…' : 'Test Connection'}
          </button>

          <button
            type="submit"
            disabled={saving || testing}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-500 text-xs font-semibold text-white shadow-sm transition-colors disabled:opacity-50"
          >
            {saving ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
            {saving ? 'Saving Changes…' : 'Save Global Provider'}
          </button>
        </div>
      </form>
    </div>
  );
}
